import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../utils/Loader";
import { useUser } from "../contexts/UserContext";
import { generateFilledPdf } from "../utils/Utility";
import { uploadCloudApi } from "../utils/cloudApi";
import { usePdf } from "../contexts/PdfContext";
import { toast } from "react-toastify";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import { useGoogleApi } from "../contexts/GoogleApiContext";

const PdfPage = () => {
  const { pdfs, loading, savePdf, removePdf, deletePdf } = usePdf();
  const { user } = useUser();
  const [uploading, setUploading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [pdfToDelete, setPdfToDelete] = useState(null);
  const navigate = useNavigate();
  const { fetchPdfFile } = useGoogleApi();

  const handleEditPdf = (pdf) => {
    if (!pdf) {
      return;
    }
    navigate(`/pdf-editor/${pdf.id}`);
  };

  const handleDownload = async (pdf) => {
    if (!pdf) return;
    setUploading(true);
    setLoadingText("Downloading …");
    try {
      const original = await fetchPdfFile(pdf.fileId);
      if (!original) return toast.error("PDF not loaded yet");
      // flatten whatever shape you stored on your PDF doc
      const loadedFields = Array.isArray(pdf.fields)
        ? pdf.fields
        : Object.values(pdf.fields).flat();

      // pass them *directly*—no need to round‐trip through React state
      const filledBytes = await generateFilledPdf(original, loadedFields);

      const blob = new Blob([filledBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdf.name || "filled"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    } finally {
      setUploading(false);
      setLoadingText("");
    }
  };

  // Handle file uploads
  const handleUpload = async (e) => {
    try {
      setUploading(true);
      setLoadingText("Uploading ...");

      const files = Array.from(e.target.files);

      // Upload only the first file (adjust if you want multiple uploads)
      const fileToUpload = files[0];
      const uploadResult = await uploadCloudApi(
        fileToUpload,
        user.uid,
        "doc",
        null
      );

      const uploadedFileName = fileToUpload.name;

      const data = {
        pdfUrl: uploadResult.fileUrl,
        uploadedBy: user.uid,
        published: false,
        pdfName: uploadedFileName,
      };

      const pdfData = await savePdf(null, data, user.uid);

      toast.success(`PDF saved with id ${pdfData.id}`);

      setUploading(false);
      setLoadingText("");
    } catch (error) {
      console.error("Upload failed:", error);
      setUploading(false);
      setLoadingText("");
    }
  };

  // Handle deletion confirmation
  const handleConfirmDelete = async () => {
    if (!pdfToDelete) return;
    setLoadingText("Deleting PDF. Please wait...");
    try {
      await deletePdf(pdfToDelete, user.uid);
      // Update local state by removing the deleted PDF
      removePdf(pdfToDelete);
      toast.success("PDF deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete PDF.");
    } finally {
      setConfirmDeleteModalOpen(false);
      setPdfToDelete(null);
      setLoadingText("");
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setPdfToDelete(null);
  };

  const pdfHasFields = (pdf) => {
    if (!pdf.fields) return false;
    // if you stored them as an array:
    if (Array.isArray(pdf.fields)) {
      return pdf.fields.length > 0;
    }
    // if you stored as an object-of-arrays:
    return Object.values(pdf.fields).flat().length > 0;
  };

  return (
    <>
      <div className="flex text-color flex-col p-2">
        {(uploading || loading) && (
          <Loader text={loadingText || "Loading PDFs..."} />
        )}

        {/* Upper Section: Upload PDF */}
        <div className="flex-1 p-4">
          <div
            data-tooltip-id="global-tooltip"
            data-tooltip-html="Sorry you can't upload here at this moment.<br/> You can upload a file to edit by going back<br/>to UP-Hub > Files. All uploaded files in UP Hub<br /> will appear here.."
            className="flex justify-center"
          >
            <label className="w-full max-w-md flex flex-col items-center px-4 py-6 bg-white/10 text-color rounded-lg shadow-lg tracking-wide uppercase border border-dark-alt cursor-pointer hover:bg-white/20">
              <span className="flex items-center space-x-2 text-base leading-normal">
                <span className="material-symbols-outlined text-3xl">
                  upload_2
                </span>
                <span>Click here to upload new PDF</span>
              </span>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleUpload}
                className="hidden"
                disabled
              />
            </label>
          </div>
        </div>

        {/* Lower Section: Display uploaded PDF cards */}
        <div className="flex-1 min-h-60 p-2 scrollable overflow-y-auto">
          <h1 className="text-2xl font-semibold mb-4">My Files</h1>
          {pdfs.length === 0 ? (
            <p className="text-drak-alt text-center">
              No PDF forms uploaded yet.
            </p>
          ) : (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {pdfs.map((pdf) => {
                const canDownload = pdfHasFields(pdf);
                return (
                  <div
                    key={pdf.id}
                    className="flex flex-col rounded-2xl bg-white/5 border border-white/10 backdrop-blur transition-all duration-200 ease-out hover:shadow-2xl hover:-translate-y-1 hover:bg-amber-400/20"
                  >
                    <div className="flex flex-col items-center rounded-lg p-2 px-4 transition">
                      <img
                        src={"/icons/file-pdf-icon.png"}
                        alt="PDF Logo"
                        className="h-12 w-auto mt-4"
                      />
                      <h2 className="mt-4 text-sm font-medium leading-snug text-center line-clamp-3 break-words">
                        {pdf.documentName}
                      </h2>
                    </div>
                    <div className="flex items-center justify-center gap-2 my-2">
                      <button
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content="Edit File"
                        onClick={() => handleEditPdf(pdf)}
                        className="btn-mini"
                      >
                        <span className="material-symbols-outlined text-sm">
                          edit
                        </span>
                      </button>

                      {canDownload && (
                        <button
                          data-tooltip-id="global-tooltip"
                          data-tooltip-content="Download File"
                          onClick={() => handleDownload(pdf)}
                          className="btn-mini"
                        >
                          <span className="material-symbols-outlined text-sm">
                            download
                          </span>
                        </button>
                      )}

                      {/* <button
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content="Delete File"
                      onClick={() => requestDelete(pdf.id)}
                      className="btn-mini"
                    >
                      <span className="material-symbols-outlined text-sm">
                        delete_outline
                      </span>
                    </button> */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={confirmDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this PDF? This action cannot be undone."
      />
    </>
  );
};

export default PdfPage;
